import cx from 'classnames'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useAppStore } from '@src/app/context'
import Header from '@src/app/onboarding/Header'
import useTitle from '@src/lib/hooks/useTitle'
import { logError } from '@src/lib/log'
import Button from '@ui/Button/Button'
import { Icon } from '@ui/Icon/Icon'
import Typography from '@ui/Typography'
// eslint-disable-next-line no-restricted-imports -- Deprecated icons should no longer be used -- https://www.notion.so/openphone/Adding-icons-46c2759e72c64013aba963654b55945b#2b999837f9c8459d8d9bd0364eb969d2
import { ContinueWithMagicCodeIcon, BrandEmailIcon } from '@ui/icons/DEPRECATED_custom'
import Spacer from '@ui/layout/Spacer'
import { themeClassNames, ThemeProvider } from '@ui/theme'

import * as styles from './UniversalLogin.css'
import type UniversalLoginAppState from './UniversalLoginAppState'
import UniversalLoginLegalFooter from './UniversalLoginLegalFooter'

import useMetaDescription from '@odyssey/hooks/useMetaDescription/useMetaDescription'

const UniversalLogin = () => {
  const navigate = useNavigate()
  const { service, electron, intent } = useAppStore()
  const [searchParams] = useSearchParams()

  useTitle('Log in')
  useMetaDescription(
    'Log back into your Quo account or create an account to get started with a Free Trial.',
  )

  const handleOpenUrl = (url: string) => {
    if (electron) {
      electron.app?.openExternal?.(url).catch(logError)
      return
    }

    window.open(url, '_self')
  }

  const getState = (): Omit<UniversalLoginAppState, 'action'> => {
    return {
      inviteToken: null,
      referralCode: null,
      couponCode: null,
      phoneNumber: null,
      intent: intent.intent,
    }
  }

  const handleLoginClick = () => {
    const email = searchParams.get('email')
    const state = getState()

    service.authV2.auth0Client
      .loginWithRedirect<UniversalLoginAppState>({
        appState: { ...state, action: 'login' },
        authorizationParams: {
          prompt: 'login',
          connection: 'Username-Password-Authentication',
          login_hint: email ?? undefined,
        },
        openUrl: handleOpenUrl,
      })
      .catch(logError)
  }

  const handleLoginClickPasswordless = () => {
    const email = searchParams.get('email')
    const state = getState()

    service.authV2.auth0Client
      .loginWithRedirect<UniversalLoginAppState>({
        appState: { ...state, action: 'passwordless_login' },
        authorizationParams: {
          prompt: 'login',
          connection: 'email',
          login_hint: email ?? undefined,
        },
        openUrl: handleOpenUrl,
      })
      .catch(logError)
  }

  const handleGoogleLogin = () => {
    const state = getState()
    service.authV2.auth0Client
      .loginWithRedirect<UniversalLoginAppState>({
        appState: { ...state, action: 'google_login' },
        authorizationParams: {
          prompt: 'select_account',
          connection: 'google-oauth2',
        },
        openUrl: handleOpenUrl,
      })
      .catch(logError)
  }

  const handleAppleLogin = () => {
    const state = getState()
    service.authV2.auth0Client
      .loginWithRedirect<UniversalLoginAppState>({
        appState: { ...state, action: 'apple_login' },
        authorizationParams: {
          connection: 'apple',
        },
        openUrl: handleOpenUrl,
      })
      .catch(logError)
  }

  const handleNavigateToSignup = () => {
    navigate('/signup')
  }

  useEffect(() => {
    service.analytics.workspace.trackPageView()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: Add missing dependencies or refactor effect (UXF-1509)
  }, [])

  return (
    <ThemeProvider themeKey="light">
      <div className={cx(themeClassNames.light, styles.container)}>
        <Header className={themeClassNames.light} />
        <div className={cx(styles.background, themeClassNames.light)}>
          <div className={styles.card}>
            {/*
            // @ts-expect-error Provide Typography with a component prop */}
            <Typography color="textPrimary" variant="largeTitle">
              Welcome back!
            </Typography>
            <Spacer height={8} />
            {/*
            // @ts-expect-error Provide Typography with a component prop */}
            <Typography variant="body" color="textSecondary">
              It's great to see you 👋
            </Typography>
            {/*
            // @ts-expect-error Provide Typography with a component prop */}
            <Typography variant="body" color="textSecondary">
              Log in to your account below
            </Typography>
            <Spacer height={24} />
            <div className={styles.actionsContainer}>
              <Button
                fullWidth
                onClick={handleGoogleLogin}
                icon={<Icon icon="brand.log-in-with-google--20" />}
                variant="outline"
              >
                <span className={styles.btnContent}>Continue with Google</span>
              </Button>
              <Button
                fullWidth
                onClick={handleAppleLogin}
                icon={<Icon icon="brand.log-in-with-apple--20" />}
                variant="outline"
              >
                <span className={styles.btnContent}>Continue with Apple</span>
              </Button>
              <Spacer height={10} />
              {/*
              // @ts-expect-error Provide Typography with a component prop */}
              <Typography variant="caption2" color="textSecondary">
                Or continue with your email
              </Typography>
              <Button
                fullWidth
                onClick={handleLoginClickPasswordless}
                icon={<ContinueWithMagicCodeIcon />}
                variant="outline"
              >
                <span className={styles.btnContent}>Email code</span>
              </Button>
              <Button
                fullWidth
                onClick={handleLoginClick}
                icon={<BrandEmailIcon />}
                variant="outline"
              >
                <span className={styles.btnContent}>Email & password</span>
              </Button>
            </div>
            <Spacer height={24} />
            {/*
            // @ts-expect-error Provide Typography with a component prop */}
            <Typography variant="callout" color="textSecondary">
              Don’t have an account yet?
            </Typography>
            <Spacer height={4} />
            <button onClick={handleNavigateToSignup} className={styles.linkButton}>
              Sign up for free
            </button>
            <Spacer height={96} />
            <UniversalLoginLegalFooter />
          </div>
        </div>
      </div>
    </ThemeProvider>
  )
}

export default observer(UniversalLogin)
